import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const FinancialDataTable = ({ data, title, subtitle }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'revenue', direction: 'desc' });
  const [expandedRows, setExpandedRows] = useState([]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const sortedData = [...data]?.sort((a, b) => {
    if (sortConfig?.direction === 'asc') {
      return a?.[sortConfig?.key] > b?.[sortConfig?.key] ? 1 : -1;
    }
    return a?.[sortConfig?.key] < b?.[sortConfig?.key] ? 1 : -1;
  });

  const toggleRow = (id) => {
    setExpandedRows(prev => 
      prev?.includes(id) ? prev?.filter(rowId => rowId !== id) : [...prev, id]
    );
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig?.key !== columnKey) {
      return <Icon name="ChevronsUpDown" size={14} className="text-muted-foreground" />;
    }
    return (
      <Icon 
        name={sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
        size={14} 
        className="text-primary" 
      />
    );
  };

  return (
    <div className="card">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
        <div>
          <h3 className="text-lg md:text-xl font-semibold text-foreground mb-1">
            {title}
          </h3>
          <p className="caption text-muted-foreground">{subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="px-3 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg transition-smooth">
            <Icon name="Filter" size={16} className="inline mr-2" />
            Filter
          </button>
          <button className="px-3 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg transition-smooth">
            <Icon name="Download" size={16} className="inline mr-2" />
            Export
          </button>
        </div>
      </div>
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-4 caption text-muted-foreground font-semibold">
                <button 
                  onClick={() => handleSort('category')}
                  className="flex items-center gap-2 hover:text-foreground transition-smooth"
                >
                  Category
                  <SortIcon columnKey="category" />
                </button>
              </th>
              <th className="text-right py-3 px-4 caption text-muted-foreground font-semibold">
                <button 
                  onClick={() => handleSort('revenue')}
                  className="flex items-center justify-end gap-2 hover:text-foreground transition-smooth ml-auto"
                >
                  Revenue
                  <SortIcon columnKey="revenue" />
                </button>
              </th>
              <th className="text-right py-3 px-4 caption text-muted-foreground font-semibold">
                <button 
                  onClick={() => handleSort('costs')}
                  className="flex items-center justify-end gap-2 hover:text-foreground transition-smooth ml-auto"
                >
                  Costs
                  <SortIcon columnKey="costs" />
                </button>
              </th>
              <th className="text-right py-3 px-4 caption text-muted-foreground font-semibold">
                <button 
                  onClick={() => handleSort('margin')}
                  className="flex items-center justify-end gap-2 hover:text-foreground transition-smooth ml-auto"
                >
                  Margin
                  <SortIcon columnKey="margin" />
                </button>
              </th>
              <th className="text-right py-3 px-4 caption text-muted-foreground font-semibold">
                <button 
                  onClick={() => handleSort('growth')}
                  className="flex items-center justify-end gap-2 hover:text-foreground transition-smooth ml-auto"
                >
                  Growth
                  <SortIcon columnKey="growth" />
                </button>
              </th>
              <th className="w-12"></th>
            </tr>
          </thead>
          <tbody>
            {sortedData?.map((row) => (
              <React.Fragment key={row?.id}>
                <tr className="border-b border-border hover:bg-muted/30 transition-smooth">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${row?.color}20` }}
                      >
                        <Icon name={row?.icon} size={16} color={row?.color} />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground">{row?.category}</p>
                        <p className="caption text-muted-foreground">{row?.subcategory}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-right">
                    <p className="text-sm font-bold text-foreground font-data">
                      ${row?.revenue?.toLocaleString()}
                    </p>
                  </td>
                  <td className="py-4 px-4 text-right">
                    <p className="text-sm font-semibold text-muted-foreground font-data">
                      ${row?.costs?.toLocaleString()}
                    </p>
                  </td>
                  <td className="py-4 px-4 text-right">
                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-semibold ${
                      row?.margin >= 30 ? 'bg-success/20 text-success' :
                      row?.margin >= 20 ? 'bg-warning/20 text-warning': 'bg-error/20 text-error'
                    }`}>
                      {row?.margin}%
                    </span>
                  </td>
                  <td className="py-4 px-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Icon 
                        name={row?.growth >= 0 ? 'TrendingUp' : 'TrendingDown'} 
                        size={14} 
                        className={row?.growth >= 0 ? 'text-success' : 'text-error'}
                      />
                      <span className={`text-sm font-semibold ${row?.growth >= 0 ? 'text-success' : 'text-error'}`}>
                        {row?.growth}%
                      </span>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <button
                      onClick={() => toggleRow(row?.id)}
                      className="p-1 rounded hover:bg-muted/50 transition-smooth"
                    >
                      <Icon 
                        name={expandedRows?.includes(row?.id) ? 'ChevronUp' : 'ChevronDown'} 
                        size={16} 
                        className="text-muted-foreground"
                      />
                    </button>
                  </td>
                </tr>
                {expandedRows?.includes(row?.id) && (
                  <tr className="bg-muted/20">
                    <td colSpan="6" className="py-4 px-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pl-11">
                        <div>
                          <p className="caption text-muted-foreground mb-1">Transaction Volume</p>
                          <p className="text-sm font-semibold text-foreground font-data">
                            {row?.details?.volume?.toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <p className="caption text-muted-foreground mb-1">Avg Transaction Value</p>
                          <p className="text-sm font-semibold text-foreground font-data">
                            ${row?.details?.avgValue?.toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <p className="caption text-muted-foreground mb-1">Cost Per Transaction</p>
                          <p className="text-sm font-semibold text-foreground font-data">
                            ${row?.details?.costPerTxn}
                          </p>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FinancialDataTable;